import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Users, Zap, TrendingUp, ArrowRight, Flame, Search, Sparkles, Brain, Film, Play, Target, DollarSign, Mic, Music, Award, ChevronRight, Rocket, Calendar, MapPin, Ticket, Radio, Shield, Heart, ShoppingBag, Video, Megaphone, Mail, Link2, BarChart3 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ProductCard from '../components/shared/ProductCard';
import { flashDealsAPI, productsAPI, publicAPI, aiAPI, videoAPI, storyAPI, projectsAPI, eventsAPI, creatorAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import SmartDashboard from '../components/guidance/SmartDashboard';

const M_THEORIES = [
  { word: "Mystery", sub: "because you never know what you're gonna get" },
  { word: "Machines", sub: "AI machines built this thing on Mars" },
  { word: "Magic", sub: "a group of magicians who coded reality" },
  { word: "Music", sub: "where the next superstar gets discovered" },
  { word: "Movement", sub: "an entire generation owns their future" },
  { word: "Money", sub: "turn $1 into your greatest investment" },
  { word: "Momentum", sub: "once it starts, it doesn't stop" },
  { word: "Manifest", sub: "dream it, fund it, build it, live it" },
];

const ICON_COMPARISONS = [
  { artist: "Beyonce", year: "1997", amount: 10, value: 50000, img: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=300", note: "Before Destiny's Child" },
  { artist: "Rihanna", year: "2003", amount: 10, value: 140000, img: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=300", note: "Discovered in Barbados" },
  { artist: "Drake", year: "2006", amount: 100, value: 250000, img: "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=300", note: "Unknown on Degrassi" },
  { artist: "Billie Eilish", year: "2016", amount: 10, value: 8000, img: "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=300", note: "13yo on SoundCloud" },
];

const PILLARS = [
  { icon: Target, title: 'Back Talent from $1', desc: 'Spot the next superstar and invest before they blow up. Our Discovery Score algorithm finds them first.', color: 'from-emerald-500 to-green-400', link: '/projects' },
  { icon: ShoppingBag, title: 'Buy & Sell Anything', desc: 'Flash deals, AI-powered search, and a real marketplace. From watches to beats — trade it all.', color: 'from-[#b8ff00] to-emerald-400', link: '/marketplace' },
  { icon: Video, title: 'Go Live & Earn', desc: 'Creator rooms with tip menus, tip races, and exploding media. Your audience pays to play.', color: 'from-rose-500 to-pink-400', link: '/creators' },
  { icon: Megaphone, title: 'Market Like a Pro', desc: 'AI campaign planner, email blasts, smart links, promoter network, and analytics — your full marketing engine.', color: 'from-violet-500 to-purple-400', link: '/register' },
];

export default function HomePage() {
  const { user } = useAuth();
  const [flashDeals, setFlashDeals] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveCount, setLiveCount] = useState(0);
  const [risingStars, setRisingStars] = useState([]);
  const [events, setEvents] = useState([]);
  const [creatorRooms, setCreatorRooms] = useState([]);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [promoVideoId, setPromoVideoId] = useState(null);
  const [showPromoVideo, setShowPromoVideo] = useState(false);
  const [promoSource, setPromoSource] = useState('video');
  const [mIndex, setMIndex] = useState(0);
  const promoVideoUrl = promoVideoId ? (promoSource === 'story' ? storyAPI.fileUrl(promoVideoId) : videoAPI.fileUrl(promoVideoId)) : '';

  useEffect(() => {
    const interval = setInterval(() => setMIndex(prev => (prev + 1) % M_THEORIES.length), 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dealsRes, productsRes, statsRes, starsRes, eventsRes, roomsRes] = await Promise.all([
          flashDealsAPI.getAll().catch(() => ({ data: [] })),
          productsAPI.getAll().catch(() => ({ data: [] })),
          publicAPI.getStats().catch(() => ({ data: {} })),
          projectsAPI.risingStars(6).catch(() => ({ data: [] })),
          eventsAPI.list({ limit: 4 }).catch(() => ({ data: [] })),
          creatorAPI.listRooms().catch(() => ({ data: [] })),
        ]);
        setFlashDeals(dealsRes.data);
        setProducts(productsRes.data);
        setStats(statsRes.data);
        setRisingStars(starsRes.data || []);
        setEvents(eventsRes.data || []);
        setCreatorRooms(roomsRes.data || []);
        setLiveCount(Math.floor(Math.random() * 500) + 200);
        if (user) {
          try { const r = await aiAPI.getRecommendations(5); setAiRecommendations(r.data.recommendations || []); } catch {}
        }
        try {
          const sp = await storyAPI.latestPromo().catch(() => null);
          if (sp?.data?.status === 'completed') { setPromoVideoId(sp.data.job_id); setPromoSource('story'); }
          else { const p = await videoAPI.latestPromo(); if (p.data.status === 'completed') { setPromoVideoId(p.data.job_id); setPromoSource('video'); } }
        } catch {}
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
    const interval = setInterval(() => setLiveCount(prev => prev + Math.floor(Math.random() * 10) - 3), 3000);
    return () => clearInterval(interval);
  }, [user]);

  const handleAISearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true); setSearchResults(null);
    try { const r = await aiAPI.smartSearch(searchQuery); setSearchResults(r.data); setAiSuggestion(r.data.suggestion || ''); } catch {} finally { setSearching(false); }
  };

  // ────────────────────── LOGGED-IN VIEW ──────────────────────
  if (user) {
    return (
      <div className="min-h-screen">
        {/* Smart Dashboard — replaces Mode Chooser + Continue + Suggested Next */}
        <SmartDashboard />

        {/* Your Spaces — universal tools */}
        <YourSpaces />

        {/* Promo Video (compact) */}
        {promoVideoUrl && (
          <section className="bg-zinc-950 border-b border-zinc-900 px-4 py-8">
            <div className="max-w-4xl mx-auto">
              {showPromoVideo ? (
                <div className="rounded-2xl overflow-hidden border border-violet-500/30 shadow-2xl" data-testid="hero-promo-expanded">
                  <video src={promoVideoUrl} controls autoPlay className="w-full" data-testid="hero-promo-video-full" />
                </div>
              ) : (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-3 backdrop-blur-sm" data-testid="hero-promo-card">
                  <div className="grid gap-4 lg:grid-cols-[1.2fr,0.8fr] lg:items-center">
                    <div className="relative overflow-hidden rounded-xl border border-white/10 bg-black aspect-[16/9]">
                      <video src={promoVideoUrl} autoPlay muted loop playsInline className="h-full w-full object-cover opacity-90" data-testid="hero-promo-video" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                      <button onClick={() => setShowPromoVideo(true)} className="absolute inset-0 flex items-center justify-center" data-testid="hero-promo-play-button">
                        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 backdrop-blur transition-transform hover:scale-110"><Play size={24} className="ml-1 text-white" /></span>
                      </button>
                    </div>
                    <div className="text-left">
                      <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-emerald-300">
                        <Film size={12} /> The M commercial
                      </span>
                      <h2 className="mt-3 font-display text-2xl font-black leading-tight text-white">Watch the story that turns mystery into momentum.</h2>
                      <button onClick={() => setShowPromoVideo(true)} className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-black hover:bg-zinc-100" data-testid="watch-m-commercial-button">
                        <Play size={14} /> Watch full commercial
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Rising Stars */}
        {risingStars.length > 0 && <RisingStarsSection stars={risingStars} />}

        {/* Events */}
        {events.length > 0 && <EventsSection events={events} />}

        {/* Creator Rooms */}
        {creatorRooms.length > 0 && <CreatorRoomsSection rooms={creatorRooms} />}

        {/* AI Recommendations */}
        {aiRecommendations.length > 0 && (
          <section className="py-10 px-4 bg-white border-t border-zinc-200">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-4 mb-6"><span className="inline-flex items-center gap-2 px-3 py-1.5 bg-violet-50 border border-violet-200 rounded-full"><Brain size={14} className="text-violet-500" /><span className="text-violet-600 text-sm font-semibold">AI Picks For You</span></span></div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">{aiRecommendations.map((p, idx) => <motion.div key={p.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08 }}><ProductCard product={p} /></motion.div>)}</div>
            </div>
          </section>
        )}

        {/* Flash Deals */}
        <FlashDealsSection deals={flashDeals} />

        {/* Products */}
        {products.length > 0 && (
          <section className="py-10 px-4 bg-zinc-50">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display font-bold text-xl text-zinc-900 flex items-center gap-2"><TrendingUp size={18} className="text-[#00aa55]" /> Trending Products</h2>
                <Link to="/marketplace" className="flex items-center gap-2 text-sm font-semibold text-[#00aa55]" data-testid="view-all-products">View All <ArrowRight size={14} /></Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">{products.slice(0, 5).map((p, idx) => <motion.div key={p.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}><ProductCard product={p} /></motion.div>)}</div>
            </div>
          </section>
        )}

        {/* Footer */}
        <FooterMystery />
      </div>
    );
  }

  // ────────────────────── GUEST VIEW ──────────────────────
  return (
    <div className="min-h-screen">
      {/* HERO */}
      <section className="relative overflow-hidden bg-zinc-950" data-testid="hero-section">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(184,255,0,0.06),transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-20 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-800 text-zinc-300 rounded-full mb-6">
              <span className="w-2 h-2 rounded-full bg-[#b8ff00] animate-pulse" />
              <span className="text-sm font-medium">{liveCount.toLocaleString()} people here right now</span>
            </div>

            <h1 className="font-display font-black text-5xl md:text-7xl lg:text-8xl mb-2 tracking-tight leading-[0.9]">
              <span className="text-white">THE </span>
              <span className="relative inline-block min-w-[200px]">
                <AnimatePresence mode="wait">
                  <motion.span key={mIndex} initial={{ opacity: 0, y: 20, rotateX: 90 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} exit={{ opacity: 0, y: -20, rotateX: -90 }} transition={{ duration: 0.4 }}
                    className="inline-block bg-gradient-to-r from-[#b8ff00] to-[#00ff88] bg-clip-text text-transparent">{M_THEORIES[mIndex].word.toUpperCase()}</motion.span>
                </AnimatePresence>
              </span>
              <br />
              <span className="text-white">IN </span>
              <span className="bg-gradient-to-r from-[#b8ff00] to-[#00ff88] bg-clip-text text-transparent">M</span>
              <span className="text-white">MARKETING</span>
            </h1>

            <AnimatePresence mode="wait">
              <motion.p key={mIndex} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-zinc-500 text-sm md:text-base italic mb-8">
                ...{M_THEORIES[mIndex].sub}
              </motion.p>
            </AnimatePresence>

            <form onSubmit={handleAISearch} className="max-w-xl mx-auto mb-6">
              <div className="relative flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                  <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder='Try "affordable headphones" or "indie music projects"...' className="pl-12 pr-4 py-6 rounded-full border border-zinc-700 bg-zinc-900 text-white text-base focus:border-[#b8ff00] placeholder:text-zinc-600" data-testid="ai-search-input" />
                </div>
                <Button type="submit" disabled={searching || !searchQuery.trim()} className="bg-[#b8ff00] text-black px-6 py-6 rounded-full font-bold hover:bg-[#a0e600]" data-testid="ai-search-btn">
                  <Sparkles size={18} className="mr-2" />{searching ? '...' : 'AI Search'}
                </Button>
              </div>
            </form>

            {searchResults && (
              <div className="max-w-4xl mx-auto mb-8 text-left">
                {aiSuggestion && <div className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-full mb-4"><Brain size={14} className="text-[#b8ff00]" /><span className="text-sm text-zinc-300">{aiSuggestion}</span></div>}
                {searchResults.products?.length > 0 && <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{searchResults.products.slice(0, 4).map(p => <ProductCard key={p.id} product={p} />)}</div>}
                <button onClick={() => { setSearchResults(null); setSearchQuery(''); }} className="text-sm text-zinc-600 hover:text-zinc-400 mt-3 underline">Clear</button>
              </div>
            )}

            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/register"><Button className="bg-gradient-to-r from-[#b8ff00] to-[#00ff88] text-black hover:shadow-lg hover:shadow-[#b8ff00]/30 font-bold px-8 py-6 text-base rounded-full" data-testid="discover-talent-btn"><Target className="mr-2" size={20} />What's Your M?</Button></Link>
              <Link to="/login"><Button variant="outline" className="border border-zinc-700 text-white hover:bg-zinc-800 px-8 py-6 text-base rounded-full font-semibold" data-testid="browse-marketplace-btn">Sign In</Button></Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SOCIAL PROOF STRIP */}
      <section className="bg-zinc-900 border-y border-zinc-800" data-testid="social-proof-strip">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center justify-center gap-8 md:gap-16 flex-wrap">
          {[
            { label: 'Projects Live', value: stats?.total_projects || risingStars.length || '9+', icon: Rocket },
            { label: 'Total Backed', value: stats?.total_invested ? formatCurrency(stats.total_invested) : '$24K+', icon: DollarSign },
            { label: 'Live Rooms', value: creatorRooms.length || '3+', icon: Radio },
            { label: 'Events', value: events.length || '4+', icon: Calendar },
          ].map((s, i) => (
            <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }} className="flex items-center gap-3 text-center">
              <s.icon size={18} className="text-[#b8ff00]" />
              <div className="text-left">
                <p className="text-white font-display font-bold text-lg leading-none">{s.value}</p>
                <p className="text-zinc-500 text-[11px]">{s.label}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3 PILLARS */}
      <section className="py-16 px-4 bg-zinc-950" data-testid="pillars-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PILLARS.map((p, idx) => (
              <motion.div key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}>
                <Link to={p.link} className="block group relative overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 p-6 hover:border-zinc-600 transition-all" data-testid={`pillar-${idx}`}>
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center mb-4`}>
                    <p.icon size={22} className="text-black" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-white mb-2">{p.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed mb-4">{p.desc}</p>
                  <span className="text-xs font-semibold text-[#b8ff00] flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                    Explore <ChevronRight size={12} />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* RISING STARS — moved UP for visual impact */}
      {risingStars.length > 0 && <RisingStarsSection stars={risingStars} />}

      {/* MARKETING ENGINE SHOWCASE */}
      <section className="py-16 px-4 bg-zinc-900 border-y border-zinc-800" data-testid="marketing-showcase">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-full text-violet-400 text-xs font-bold tracking-wider uppercase mb-4">
              <Megaphone size={12} /> Marketing Engine
            </span>
            <h2 className="font-display font-black text-3xl md:text-4xl text-white mb-3">
              Build it. <span className="text-[#b8ff00]">Market it.</span> Own it.
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">The first platform where you create, sell, fund, AND market — all under one roof. No Mailchimp. No Linktree. No separate tools.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Sparkles, title: 'AI Campaign Planner', desc: 'Tell us your goal. AI generates a 7-day marketing plan with content, timing, and boost strategy.', color: 'text-violet-400', bg: 'bg-violet-500/10' },
              { icon: Link2, title: 'Smart Links', desc: 'One link for everything — your projects, products, events, and live rooms. Share it everywhere.', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
              { icon: Mail, title: 'Email Your Fans', desc: 'Blast followers, backers, or everyone. Set up automations for new followers and backers.', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
              { icon: Users, title: 'Promoter Network', desc: 'Post briefs, set commissions. Promoters pick them up and earn by driving your sales.', color: 'text-rose-400', bg: 'bg-rose-500/10' },
              { icon: BarChart3, title: 'Full Analytics', desc: 'Revenue, followers, backers, email opens, conversion funnels — know what\'s working.', color: 'text-amber-400', bg: 'bg-amber-500/10' },
              { icon: Zap, title: 'Boost System', desc: 'Spend wallet balance to boost campaigns on the homepage and discovery feeds.', color: 'text-[#b8ff00]', bg: 'bg-[#b8ff00]/10' },
            ].map((item, idx) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.06 }}
                className="p-5 bg-zinc-800/50 border border-zinc-700/50 rounded-2xl hover:border-zinc-600 transition-all">
                <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center mb-3`}>
                  <item.icon size={18} className={item.color} />
                </div>
                <h3 className="font-bold text-white text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-zinc-400 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/register">
              <Button className="bg-gradient-to-r from-violet-600 to-purple-600 text-white font-bold px-8 py-5 rounded-full hover:shadow-lg hover:shadow-violet-500/20 transition-all" data-testid="marketing-cta-btn">
                <Megaphone size={16} className="mr-2" /> Start Marketing for Free
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* MICRO-INVESTING PITCH (condensed) */}
      <section className="py-16 px-4 bg-white" data-testid="micro-invest-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[1fr,1fr] gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-100 border border-zinc-200 rounded-full text-zinc-700 text-xs font-bold tracking-wider uppercase mb-4">
                <DollarSign size={12} className="text-[#00aa55]" /> Fan Micro-Investing
              </span>
              <h2 className="font-display font-black text-3xl md:text-4xl text-zinc-900 mb-3 leading-tight">
                What if you invested <span className="text-[#00aa55]">$10</span> in Beyonce before anyone knew her name?
              </h2>
              <p className="text-zinc-500 text-base mb-6">
                Every superstar was once unknown. The next Rihanna, the next Drake — they're here right now.
                <span className="font-semibold text-zinc-800"> Back them from $1.</span>
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/projects"><Button className="bg-zinc-900 text-white font-bold px-8 py-5 rounded-full hover:bg-zinc-800" data-testid="invest-now-btn"><Rocket className="mr-2" size={18} />Start Backing</Button></Link>
                <Link to="/register"><Button variant="outline" className="border border-zinc-300 text-zinc-700 px-8 py-5 rounded-full font-semibold" data-testid="become-creator-btn"><Mic className="mr-2" size={16} />Launch Yours</Button></Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {ICON_COMPARISONS.map((comp, idx) => (
                <motion.div key={comp.artist} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08 }}
                  className="relative overflow-hidden rounded-xl border border-zinc-200 bg-white group hover:shadow-lg transition-all">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={comp.img} alt={comp.artist} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-white via-white/70 to-transparent" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-[10px] text-zinc-400">{comp.note} ({comp.year})</p>
                    <p className="text-zinc-700 font-bold text-xs mb-1">$10 in {comp.artist}</p>
                    <span className="font-display font-black text-xl text-[#00aa55]">${comp.value.toLocaleString()}</span>
                    <span className="text-[10px] text-[#00aa55]/70 font-mono font-bold ml-1">{Math.round(comp.value / comp.amount).toLocaleString()}x</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EVENTS */}
      {events.length > 0 && <EventsSection events={events} />}

      {/* CREATOR ROOMS — showcase the new features */}
      {creatorRooms.length > 0 && <CreatorRoomsSection rooms={creatorRooms} />}

      {/* LOCAL SCENE */}
      <section className="py-14 px-4 bg-white" data-testid="local-scene-home-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid gap-8 lg:grid-cols-[1fr,1fr] items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-emerald-700">
                <MapPin size={12} /> Local Scene
              </span>
              <h2 className="mt-4 font-display text-3xl font-black leading-tight text-zinc-900">Turn one city into a movement.</h2>
              <p className="mt-3 text-sm text-zinc-500 leading-relaxed">
                Rank artists, spot venues, and get outreach playbooks that push local momentum into funded projects.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {['Atlanta', 'Los Angeles', 'London', 'Lagos'].map((city) => (
                  <Link key={city} to={`/local-scene?city=${encodeURIComponent(city)}`} className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-sm font-semibold text-zinc-600 hover:border-emerald-300 hover:text-emerald-700 transition-colors" data-testid={`local-scene-home-city-${city.toLowerCase()}`}>
                    {city}
                  </Link>
                ))}
              </div>
              <div className="mt-5">
                <Link to="/local-scene?city=Atlanta"><Button className="rounded-full bg-zinc-900 px-6 py-4 font-bold text-white hover:bg-zinc-800" data-testid="open-local-scene-board-btn">Open city board <ArrowRight size={14} className="ml-2" /></Button></Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { title: 'Leaderboard', desc: 'Rank artists by momentum.', icon: TrendingUp, accent: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
                { title: 'Venue hotspots', desc: 'Where attention clusters.', icon: MapPin, accent: 'text-amber-600 bg-amber-50 border-amber-200' },
                { title: 'Outreach angles', desc: 'Hooks and CTAs for push.', icon: Mic, accent: 'text-violet-600 bg-violet-50 border-violet-200' },
                { title: 'Launch plays', desc: 'Content to funding bridge.', icon: Rocket, accent: 'text-blue-600 bg-blue-50 border-blue-200' },
              ].map((item, index) => (
                <motion.div key={item.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.06 }} className={`rounded-2xl border p-4 ${item.accent}`}>
                  <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white"><item.icon size={18} /></div>
                  <h3 className="font-display text-base font-bold text-zinc-900">{item.title}</h3>
                  <p className="mt-1 text-xs leading-relaxed text-zinc-600">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FLASH DEALS */}
      <FlashDealsSection deals={flashDeals} />

      {/* FINAL CTA */}
      <section className="py-20 px-4 bg-black relative overflow-hidden" data-testid="whats-your-m-cta">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#b8ff00] rounded-full blur-[180px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500 rounded-full blur-[180px]" />
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-display font-black text-5xl md:text-6xl text-white mb-4">
              What does the <span className="text-[#b8ff00]">M</span> mean to you?
            </h2>
            <p className="text-lg text-zinc-400 mb-3">Go live. Sell something. Get discovered. Back talent. Market your business.</p>
            <p className="text-base text-zinc-500 mb-8">All of it happens here. Choose your path.</p>
            <Link to="/register">
              <Button className="bg-[#b8ff00] text-black font-bold px-12 py-7 text-lg rounded-full hover:bg-[#a0e600] hover:shadow-lg hover:shadow-[#b8ff00]/30 transition-all" data-testid="whats-your-m-cta-btn">
                Enter MMarketing <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Promo Video */}
      {promoVideoId && (
        <section className="py-12 px-4 bg-zinc-950 border-t border-zinc-800">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-full text-violet-400 text-xs font-semibold mb-4"><Film size={12} /> Powered by Sora 2 AI</span>
            <h2 className="font-display font-bold text-2xl text-white mb-4">Watch the M Commercial</h2>
            {showPromoVideo ? (
              <div className="rounded-2xl overflow-hidden border border-violet-500/30 shadow-2xl"><video src={promoVideoUrl} controls autoPlay className="w-full" data-testid="promo-video-player" /></div>
            ) : (
              <button onClick={() => setShowPromoVideo(true)} className="group relative w-full max-w-2xl mx-auto aspect-video rounded-2xl overflow-hidden border border-violet-500/20 bg-zinc-900 hover:border-violet-400 transition-all" data-testid="play-promo-video-btn">
                <video src={promoVideoUrl} autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover opacity-75" data-testid="promo-video-teaser" />
                <div className="absolute inset-0 flex items-center justify-center"><div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur flex items-center justify-center group-hover:bg-white/20 group-hover:scale-110 transition-all"><Play size={28} className="text-white ml-1" /></div></div>
              </button>
            )}
          </div>
        </section>
      )}

      <FooterMystery />
    </div>
  );
}

// ─── Shared Components ───

function RisingStarsSection({ stars }) {
  return (
    <section className="py-14 px-4 bg-zinc-50" data-testid="rising-stars-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 border border-orange-200 rounded-full">
                <Flame size={12} className="text-orange-500" /><span className="text-orange-600 text-xs font-bold">Rising Stars</span>
              </span>
            </div>
            <h2 className="font-display font-bold text-2xl text-zinc-900">Trending Projects</h2>
          </div>
          <Link to="/projects" className="flex items-center gap-2 text-sm font-semibold text-[#00aa55]" data-testid="view-all-projects-link">View All <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {stars.slice(0, 6).map((project, idx) => {
            const pct = project.funded_pct || 0;
            const score = project.discovery_score || 0;
            return (
              <motion.div key={project.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.06 }}>
                <Link to={`/projects/${project.id}`} className="block rounded-2xl overflow-hidden bg-white border border-zinc-200 hover:border-[#00aa55]/40 hover:shadow-xl group transition-all" data-testid={`rising-star-${project.id}`}>
                  {project.cover_image && (
                    <div className="aspect-[16/9] overflow-hidden relative">
                      <img src={project.cover_image} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      {score > 0 && (
                        <div className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-white/10">
                          <Target size={11} className="text-[#b8ff00]" /><span className="text-xs font-bold text-[#b8ff00]">{score}</span>
                        </div>
                      )}
                      <div className="absolute bottom-3 left-3 flex items-center gap-2">
                        {project.creator_avatar && <img src={project.creator_avatar} alt="" className="w-7 h-7 rounded-full border-2 border-white object-cover" />}
                        <div>
                          <p className="text-white text-xs font-bold">{project.creator_name}</p>
                          <p className="text-white/60 text-[10px]">{project.genre}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-display font-bold text-base text-zinc-900 mb-1 group-hover:text-[#00aa55] transition-colors">{project.title}</h3>
                    <p className="text-xs text-zinc-500 line-clamp-2 mb-3">{project.description}</p>
                    <div className="mb-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-zinc-800 font-semibold">{formatCurrency(project.current_funding || 0)}</span>
                        <span className="text-zinc-400">{formatCurrency(project.funding_goal)}</span>
                      </div>
                      <div className="w-full bg-zinc-100 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full ${pct >= 80 ? 'bg-gradient-to-r from-[#00aa55] to-[#b8ff00]' : 'bg-[#00aa55]'}`} style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-zinc-500">
                      <span className="flex items-center gap-1"><Users size={11} /> {project.backer_count || 0} backers</span>
                      <span className="text-[#00aa55] font-semibold flex items-center gap-1">Back from $1 <ChevronRight size={12} /></span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function EventsSection({ events }) {
  return (
    <section className="py-14 px-4 bg-zinc-950" data-testid="events-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full mb-1">
              <Calendar size={12} className="text-amber-400" /><span className="text-amber-400 text-xs font-bold">Live Events</span>
            </span>
            <h2 className="font-display font-bold text-2xl text-white">Upcoming Events</h2>
          </div>
          <Link to="/events" className="flex items-center gap-2 text-sm font-semibold text-amber-400" data-testid="view-all-events-link">View All <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {events.slice(0, 4).map((event, idx) => (
            <motion.div key={event.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08 }}>
              <Link to={`/events/${event.id}`} className="block p-5 rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-amber-500/40 group transition-all" data-testid={`event-card-home-${event.id}`}>
                <div className="flex items-center gap-2 mb-3">
                  {event.creator_avatar && <img src={event.creator_avatar} alt="" className="w-6 h-6 rounded-full border border-zinc-700 object-cover" />}
                  <span className="text-xs text-zinc-500">{event.creator_name}</span>
                </div>
                <h3 className="font-bold text-white text-sm mb-2 group-hover:text-amber-400 transition-colors">{event.title}</h3>
                <div className="space-y-1.5 text-xs text-zinc-400">
                  <div className="flex items-center gap-2"><Calendar size={12} className="text-amber-400" /> {event.event_date}</div>
                  <div className="flex items-center gap-2"><MapPin size={12} className="text-amber-400" /> {event.venue || event.location}</div>
                  <div className="flex items-center gap-2"><Ticket size={12} className="text-amber-400" /> {event.total_tickets_sold} sold</div>
                </div>
                {event.ticket_tiers?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between text-xs">
                    <span className="text-zinc-500">From <span className="text-amber-400 font-bold">${Math.min(...event.ticket_tiers.map(t => t.price))}</span></span>
                    <span className="text-amber-400 font-semibold flex items-center gap-1 group-hover:translate-x-1 transition-transform">Attend <ChevronRight size={12} /></span>
                  </div>
                )}
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CreatorRoomsSection({ rooms }) {
  return (
    <section className="py-14 px-4 bg-zinc-900" data-testid="creator-rooms-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-rose-500/10 border border-rose-500/20 rounded-full mb-1">
              <Radio size={12} className="text-rose-400" /><span className="text-rose-400 text-xs font-bold">Creator Rooms</span>
            </span>
            <h2 className="font-display font-bold text-2xl text-white">Go Live, Get Tipped, Get Paid</h2>
            <p className="text-zinc-500 text-sm mt-1">Tip menus, tip races, exploding media — every room is a stage.</p>
          </div>
          <Link to="/creators" className="flex items-center gap-2 text-sm font-semibold text-rose-400" data-testid="view-all-rooms-link">All Rooms <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rooms.slice(0, 3).map((room, idx) => (
            <motion.div key={room.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.08 }}>
              <Link to={`/creator/${room.id}`} className="block p-5 rounded-2xl bg-zinc-800/50 border border-zinc-700 hover:border-rose-500/40 group transition-all" data-testid={`room-card-${room.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${room.is_adult ? 'bg-rose-900/50' : 'bg-violet-900/50'}`}>
                      <Radio size={14} className={room.is_adult ? 'text-rose-400' : 'text-violet-400'} />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-white group-hover:text-rose-400 transition-colors">{room.name}</h3>
                      <p className="text-[11px] text-zinc-500">{room.creator_name}</p>
                    </div>
                  </div>
                  {room.is_live && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-red-500/20 rounded-full">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                      <span className="text-[10px] text-red-400 font-bold">LIVE</span>
                    </span>
                  )}
                </div>
                {room.preview_text && <p className="text-xs text-zinc-400 italic mb-3">"{room.preview_text}"</p>}
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-3 text-zinc-500">
                    <span className="flex items-center gap-1"><Users size={11} /> {room.subscriber_count} subs</span>
                    <span className="flex items-center gap-1"><Heart size={11} /> ${(room.total_tips || 0).toFixed(0)} tips</span>
                  </div>
                  <span className="text-rose-400 font-bold">{formatCurrency(room.price_per_month)}/mo</span>
                </div>
                <div className="mt-3 pt-3 border-t border-zinc-700/50 flex items-center gap-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-700/50 text-zinc-400">{room.category}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-700/50 text-zinc-400">{room.is_broadcast ? 'Broadcast' : '1-on-1'}</span>
                  <span className="ml-auto text-[10px] text-zinc-500 flex items-center gap-1"><Shield size={10} className="text-green-400" /> E2E</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function YourSpaces() {
  const ALL_SPACES = [
    { label: 'Marketing', desc: 'Campaigns, email, smart links', path: '/marketing', icon: Megaphone, gradient: 'from-violet-500 to-purple-600' },
    { label: 'AI Studio', desc: 'Generate music, art, content', path: '/studio', icon: Sparkles, gradient: 'from-fuchsia-500 to-pink-600' },
    { label: 'Shop', desc: 'Browse deals & products', path: '/marketplace', icon: ShoppingBag, gradient: 'from-emerald-500 to-green-600' },
    { label: 'Back Artists', desc: 'Invest from $1', path: '/projects', icon: Target, gradient: 'from-amber-500 to-orange-500' },
    { label: 'Events', desc: 'Discover live shows', path: '/events', icon: Calendar, gradient: 'from-rose-500 to-pink-600' },
    { label: 'Go Live', desc: 'Stream, earn tips', path: '/creators', icon: Radio, gradient: 'from-cyan-500 to-blue-500' },
    { label: 'Launch Project', desc: 'Start a funding campaign', path: '/projects/create', icon: Rocket, gradient: 'from-emerald-500 to-green-600' },
    { label: 'My Orders', desc: 'Track purchases', path: '/orders', icon: Clock, gradient: 'from-blue-500 to-indigo-600' },
  ];

  return (
    <section className="py-8 px-4 bg-zinc-50 border-b border-zinc-200" data-testid="your-spaces">
      <div className="max-w-5xl mx-auto">
        <h2 className="font-display font-bold text-lg text-zinc-900 mb-4">Your Spaces</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {ALL_SPACES.map((space, idx) => {
            const Icon = space.icon;
            return (
              <motion.div key={space.path} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                <Link to={space.path} className="block p-4 rounded-xl bg-white border border-zinc-200 hover:border-zinc-300 hover:shadow-md group transition-all" data-testid={`space-${space.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${space.gradient} flex items-center justify-center mb-3`}>
                    <Icon size={16} className="text-white" />
                  </div>
                  <h3 className="text-sm font-bold text-zinc-900 group-hover:text-[#00aa55] transition-colors">{space.label}</h3>
                  <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{space.desc}</p>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FlashDealsSection({ deals }) {
  if (!deals || deals.length === 0) return null;
  const displayDeals = deals.slice(0, 4);
  return (
    <section className="py-14 px-4 bg-zinc-50" data-testid="flash-deals-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center gap-2 px-3 py-1 bg-red-100 border border-red-200 rounded-full"><span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /><span className="text-red-600 text-xs font-bold tracking-wider uppercase">Live Now</span></div>
            </div>
            <h2 className="font-display font-bold text-2xl text-zinc-900">Flash Deals</h2>
          </div>
          <Link to="/live" className="flex items-center gap-2 text-sm font-semibold text-[#00aa55]" data-testid="view-all-deals">View All <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {displayDeals.map((d, idx) => {
            const claimsPercent = (d.claims_count / d.max_claims) * 100;
            const isUrgent = claimsPercent > 70;
            const endTime = new Date(d.end_time);
            const minsLeft = Math.max(0, Math.round((endTime - Date.now()) / 60000));
            const timeLabel = minsLeft < 60 ? `${minsLeft}m left` : `${Math.round(minsLeft / 60)}h left`;
            return (
              <motion.div key={d.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.1 }}>
                <Link to={`/flash-deal/${d.id}`} className="block rounded-2xl overflow-hidden bg-white border border-zinc-200 hover:shadow-xl group transition-all" data-testid={`flash-deal-card-${d.id}`}>
                  <div className="relative aspect-square overflow-hidden">
                    {d.product_image ? <img src={d.product_image} alt={d.product_name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" /> : <div className="w-full h-full flex items-center justify-center bg-zinc-100"><Zap size={48} className="text-zinc-300" /></div>}
                    <div className="absolute top-3 left-3 px-2.5 py-1 bg-red-500 text-white rounded-full"><span className="font-display font-bold text-xs">-{d.discount_percent}%</span></div>
                    <div className={`absolute top-3 right-3 px-2.5 py-1 rounded-full flex items-center gap-1 ${isUrgent ? 'bg-red-500 text-white animate-pulse' : 'bg-black/70 backdrop-blur text-white'}`}><Clock size={10} /><span className="font-mono text-[11px] font-bold">{timeLabel}</span></div>
                  </div>
                  <div className="p-3">
                    <p className="text-[11px] text-zinc-400 mb-0.5">by {d.vendor_name}</p>
                    <h3 className="font-semibold text-zinc-900 text-sm truncate mb-2">{d.product_name}</h3>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="font-display font-bold text-lg text-[#00aa55]">${d.flash_price}</span>
                      <span className="text-xs text-zinc-400 line-through">${d.original_price}</span>
                      <span className="text-[10px] font-bold text-red-500 ml-auto">SAVE ${d.original_price - d.flash_price}</span>
                    </div>
                    <div className="w-full bg-zinc-100 rounded-full h-1">
                      <div className={`h-1 rounded-full ${isUrgent ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'bg-[#00aa55]'}`} style={{ width: `${claimsPercent}%` }} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FooterMystery() {
  return (
    <section className="py-10 px-4 bg-zinc-950 border-t border-zinc-800">
      <div className="max-w-3xl mx-auto text-center">
        <p className="text-zinc-600 text-sm mb-2">So... what does the <span className="font-bold text-zinc-400">M</span> really stand for?</p>
        <p className="text-zinc-500 text-xs italic">"I heard AI machines built the whole thing on Mars." "No, it's a group of magicians." "Nah, it stands for Money."</p>
        <p className="mt-3 text-zinc-700 text-xs">The truth? <span className="font-bold text-[#b8ff00]">You decide.</span></p>
      </div>
    </section>
  );
}
